// ============================================================
// ARMY DATA — deployment serialization & save slots
// ============================================================
//
// 負責將玩家的佈陣（單位類型、位置、路徑）序列化為 JSON，
// 存入 localStorage（之後可改為 Server 端）。
// 提供 3 組手動存檔 slot + 1 個自動存檔 key。
//
// 版本相容：當資料結構變更時，遞增 ARMY_VERSION；
// loadSlot / loadAutosave 會拒絕版本不符的舊檔。
// ============================================================

import { BATTLE_COLS, BATTLE_ROWS, UCFG, BASES } from './constants.ts';
import { DeployedUnit } from './Deployment.ts';
import { Pos } from './BattleUnit.ts';

// 注意：composition cap（兵種上限）不在這層檢查 — 此層只負責結構驗證
// （version、type 已知、座標在玩家區、path 鄰接）。Composition cap 由
// Deployment.loadFromUnits 在 manual 模式下統一強制（讀當前 rules）。
// 這樣切換 PvP mode（3v3 / 5v5 / 10v10）時，存檔不會因為過時的 cap 卡住。

/** Schema version — bump when breaking changes happen. 不符版本的舊檔
 *  會被 `validatePayload` 拒絕，使用者拿到乾淨佈陣。
 *  v4：移除 LEADERS 領軍系統，去掉 `leader` 欄位。 */
export const ARMY_VERSION = 4;

/** Number of manual save slots exposed in the UI */
export const SLOT_COUNT = 3;

/** localStorage key prefix for all army-related entries */
const LS_PREFIX = 'pt.army.';
const LS_AUTOSAVE = LS_PREFIX + 'autosave';
const lsSlotKey = (slot: number) => `${LS_PREFIX}slot.${slot}`;

/** Base-tile lookup — mirrors Deployment.ts (can't share BASE_TILES because it's private) */
const BASE_TILES = new Set<string>();
for (const b of BASES) {
  for (const t of b.tiles) BASE_TILES.add(`${t.x},${t.y}`);
}

/** Player placeable rows (must match Deployment.ts) */
const PLAYER_MIN_ROW = 6;
const PLAYER_MAX_ROW = 7;

/** Serialized unit shape written to storage */
export interface SavedUnit {
  type: string;
  pos: Pos;
  path: Pos[];
}

/** Top-level save payload */
export interface ArmyData {
  version: number;
  savedAt: number;
  units: SavedUnit[];
}

// ------------------------------------------------------------
// SERIALIZATION
// ------------------------------------------------------------

/** Build an ArmyData snapshot from the player's currently placed units */
export function serialize(placed: DeployedUnit[]): ArmyData {
  return {
    version: ARMY_VERSION,
    savedAt: Date.now(),
    units: placed.map(u => ({
      type: u.type,
      pos: { x: u.pos.x, y: u.pos.y },
      path: u.path.map(p => ({ x: p.x, y: p.y })),
    })),
  };
}

// ------------------------------------------------------------
// VALIDATION + DESERIALIZATION
// ------------------------------------------------------------

/** Validate & deep-copy a SavedUnit. Returns null on any invalid field. */
function validateUnit(raw: any, occupied: Set<string>): SavedUnit | null {
  if (!raw || typeof raw !== 'object') return null;
  if (typeof raw.type !== 'string') return null;
  if (!UCFG[raw.type]) return null;
  if (!raw.pos || typeof raw.pos.x !== 'number' || typeof raw.pos.y !== 'number') return null;

  const { x, y } = raw.pos;
  if (!Number.isInteger(x) || !Number.isInteger(y)) return null;
  if (x < 0 || x >= BATTLE_COLS) return null;
  if (y < PLAYER_MIN_ROW || y > PLAYER_MAX_ROW) return null;
  if (BASE_TILES.has(`${x},${y}`)) return null;

  const posKey = `${x},${y}`;
  if (occupied.has(posKey)) return null;
  occupied.add(posKey);

  if (!Array.isArray(raw.path)) return null;
  const cfg = UCFG[raw.type];
  if (raw.path.length > cfg.maxPath) return null;

  const path: Pos[] = [];
  let last: Pos = { x, y };
  for (const step of raw.path) {
    if (!step || typeof step.x !== 'number' || typeof step.y !== 'number') return null;
    if (!Number.isInteger(step.x) || !Number.isInteger(step.y)) return null;
    if (step.x < 0 || step.x >= BATTLE_COLS) return null;
    if (step.y < 0 || step.y >= BATTLE_ROWS) return null;
    if (BASE_TILES.has(`${step.x},${step.y}`)) return null;
    const dx = Math.abs(step.x - last.x), dy = Math.abs(step.y - last.y);
    if (dx + dy !== 1) return null; // must be Manhattan-adjacent
    path.push({ x: step.x, y: step.y });
    last = path[path.length - 1];
  }

  return { type: raw.type, pos: { x, y }, path };
}

/** Validate the top-level payload shape (version + units array). */
function validatePayload(raw: any): ArmyData | null {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.version !== ARMY_VERSION) return null;
  if (!Array.isArray(raw.units)) return null;

  const occupied = new Set<string>();
  const units: SavedUnit[] = [];
  for (const u of raw.units) {
    const v = validateUnit(u, occupied);
    if (!v) return null;
    units.push(v);
  }

  // Composition cap intentionally not checked here — see file header.
  // Deployment.loadFromUnits (manual mode) is the single source of truth
  // for composition caps; it reads the active rules.composition.

  return {
    version: ARMY_VERSION,
    savedAt: typeof raw.savedAt === 'number' ? raw.savedAt : Date.now(),
    units,
  };
}

/** Convert a validated ArmyData into DeployedUnit[] for Deployment.loadFromUnits */
export function toDeployedUnits(data: ArmyData): DeployedUnit[] {
  return data.units.map(u => ({
    type: u.type,
    pos: { x: u.pos.x, y: u.pos.y },
    path: u.path.map(p => ({ x: p.x, y: p.y })),
  }));
}

// ------------------------------------------------------------
// LOCALSTORAGE CRUD
// ------------------------------------------------------------

/** Safely read + parse a localStorage entry; returns null on any failure */
function readRaw(key: string): any {
  try {
    const s = localStorage.getItem(key);
    if (!s) return null;
    return JSON.parse(s);
  } catch (e) {
    console.warn('[ArmyData] parse failed for', key, e);
    return null;
  }
}

/** Safely write a JSON value; swallows QuotaExceeded so callers don't crash */
function writeRaw(key: string, data: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    console.warn('[ArmyData] write failed for', key, e);
    return false;
  }
}

/** Save to manual slot N (0..SLOT_COUNT-1). Returns success flag. */
export function saveSlot(slot: number, data: ArmyData): boolean {
  if (slot < 0 || slot >= SLOT_COUNT) return false;
  return writeRaw(lsSlotKey(slot), data);
}

/** Load manual slot N. Returns null if empty or invalid. */
export function loadSlot(slot: number): ArmyData | null {
  if (slot < 0 || slot >= SLOT_COUNT) return null;
  const raw = readRaw(lsSlotKey(slot));
  if (!raw) return null;
  const data = validatePayload(raw);
  if (!data) {
    console.warn('[ArmyData] invalid / incompatible slot', slot, '- ignored');
    return null;
  }
  return data;
}

/** Clear manual slot N. */
export function clearSlot(slot: number): void {
  if (slot < 0 || slot >= SLOT_COUNT) return;
  try { localStorage.removeItem(lsSlotKey(slot)); } catch { /* ignore */ }
}

/** Enumerate all slots — index = slot, null = empty/invalid */
export function listSlots(): (ArmyData | null)[] {
  const out: (ArmyData | null)[] = [];
  for (let i = 0; i < SLOT_COUNT; i++) out.push(loadSlot(i));
  return out;
}

/** Save the current deployment into the autosave key (separate from manual slots). */
export function saveAutosave(data: ArmyData): boolean {
  return writeRaw(LS_AUTOSAVE, data);
}

/** Load the autosave. Returns null if empty / invalid. */
export function loadAutosave(): ArmyData | null {
  const raw = readRaw(LS_AUTOSAVE);
  if (!raw) return null;
  const data = validatePayload(raw);
  if (!data) {
    console.warn('[ArmyData] invalid / incompatible autosave - ignored');
    return null;
  }
  return data;
}

/** Clear the autosave. */
export function clearAutosave(): void {
  try { localStorage.removeItem(LS_AUTOSAVE); } catch { /* ignore */ }
}
